import React, { useState, useEffect } from 'react'
import { createFormBag, FormBag, FormData, FormUpdateEvent } from 'react-formage'

import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { stopSchema } from 'domains/org/getty/schemas/stop'
import { GettyAppState, GettyStore } from 'domains/org/getty/store'

import { ButtonGroup, Button } from 'components/ui/button'
import { CheckboxInputElement } from 'components/ui/checkbox'
import { InfiniteScroll } from 'components/ui/infinite-scroll'
import { IconList } from 'components/ui/icons'
import { Input } from 'components/ui/input'
import { Description, Image, Title, ListItemLabel, Subtitle } from 'components/ui/list-item-label'
import { Modal } from 'components/ui/modal'
import { usePagination } from 'components/pagination'

import { AttributeRef } from 'generated/mos/entity'
import { ListStopsRequest } from 'generated/mos/org/getty/audioaccess'
import { Stop } from 'generated/mos/org/getty/audio'
import { Media } from 'generated/mos/media';
import { Page } from 'generated/mos/pagination'

import { bffEndpoint } from 'helpers/bff'
import { pick, Invariant } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'

import styled from 'styled'

type ActionProps = Pick<GettyActionCreators, 'gettyListStopsRequest' | 'gettyListStopsReset' | 'gettyListStopsSearchRequest' >
type ConnectedProps = Pick<GettyAppState, 'stops' | 'stopUpdate'>
type DirectProps = {
  readonly currentRelatedStops: ReadonlyArray<Stop.Ref>;
  readonly onCancel: () => void;
  readonly onComplete: (updatedRelatedStopRefs: ReadonlyArray<Stop.Entity>) => void;
  readonly projectId?: string;
  readonly historyPush?: (id: string) => void;
  readonly navBackRoute?: string;
}

type Props = ActionProps & ConnectedProps & DirectProps;

type FormValues = {
  search: string;
}

export const SearchContainer = styled.div`
  margin-bottom: ${space(4)};
  position: relative;
`

export const SearchTools = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 36px;
  margin: 0 0 ${space(4)};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: 500;
  text-transform: uppercase;
`

export const SelectRelatedStopsView = (props: Props) => {
  const {
    currentRelatedStops,
    gettyListStopsRequest,
    gettyListStopsReset,
    gettyListStopsSearchRequest,
    onComplete,
    onCancel,
    stops,
    stopUpdate,
  } = props

  const [ selectedRelatedStops, setSelectedRelatedStops ] = useState<ReadonlyArray<Stop.Entity>>([])
  const bag: FormBag<FormValues> = createFormBag({ search: '' })
  const [ formBag, setFormBag ] = useState<FormBag<FormValues>>(bag)
  const [ typingTimeout, setTypingTimeout ] = useState<NodeJS.Timeout | undefined>(undefined)
  const [ storedSearchTerm, setStoredSearchTerm ] = useState('')

  const stopData = statusSelector.data(stopUpdate)
  const stop = stopData ? stopData.stop : undefined
  const refsToExclude = stop && stop.ref ? [...currentRelatedStops, stop.ref] : []

  const pagination = usePagination({ pageSize: 32 })

  const hostName = bffEndpoint(window.location.hostname)

  useEffect(() => {
    gettyListStopsReset()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const requestPageData = () => {
    if(statusSelector.hasData(stops) &&
    stops.data.entityList.length < stops.data.totalItems) {
      const nextPage = stops.data.nextPage
      gettyListStopsRequest({
        ...ListStopsRequest.defaults,
        page: nextPage,
        filter: {
          ...ListStopsRequest.ListStopFilter.defaults,
          excludeByRefs: refsToExclude,
          searchText: storedSearchTerm
        },
      },
      true)
    }
  }

  useEffect(() => {
    if (stops.status === Status.Idle || stops.status === Status.Ready) {
      gettyListStopsSearchRequest({
        ...ListStopsRequest.defaults,
        page: {
          ...Page.defaults,
          skipItems: pagination.skipItems,
          pageSize: pagination.pageSize,
        },
        filter: {
          ...ListStopsRequest.ListStopFilter.defaults,
          excludeByRefs: refsToExclude,
          searchText: storedSearchTerm
        },
      })
    }
  },[storedSearchTerm]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = () => {
    onComplete(selectedRelatedStops)
    onCancel()
  }

  const includes = (stopToFind?: Stop.Entity): boolean => {
    if (!stopToFind) return false;
    return !!selectedRelatedStops.filter((stop) => stop === stopToFind).length;
  };

  const toggle = (toggleStop?: Stop.Entity) => {
    if (!toggleStop) return;
    if (selectedRelatedStops.includes(toggleStop)) {
      setSelectedRelatedStops(selectedRelatedStops.filter(stop => stop !== toggleStop));
    } else {
      setSelectedRelatedStops([ ...selectedRelatedStops, toggleStop ]);
    }
  };

  const listItem = (stop: Stop.Entity) => {
    const selected = stop && stop.ref && includes(stop)
    const title: any = stopSchema.title.read(stop.title)
    const producedBy: any = stopSchema.producedBy.read(stop.producedBy)

    const stopImage = stopSchema.image.read(stop.image) as Media.Ref
    const stopImageId = stopImage && stopImage.id

    const imagePath = stopImageId && hostName ? `${hostName}/images/${stopImageId}.jpg?s=crop&h=92&w=92` : ''

    // check if attribute refs are still present and throw invariant if they are
    if (title && title.type === AttributeRef.refName || producedBy && producedBy.type === AttributeRef.refName) {
      throw new Invariant('Attribute ref did not resolve to a string value')
    }

    return (
      <ListItemLabel key={Stop.mustRef(stop.ref).id}>
        <CheckboxInputElement
          checked={selected}
          onClick={() => toggle(stop)}
        />
        <Image src={imagePath} />
        <Description>
          <Title>{title}</Title>
          <Subtitle>{producedBy ? producedBy : '-'}</Subtitle>
        </Description>
      </ListItemLabel>
    )
  }

  const renderEntityStyles = {
      icon: IconList,
      name: 'list',
      render: listItem,
  }

  const onFormUpdate = (event: FormUpdateEvent<FormValues>) => {
    setFormBag(event.bag)

    const newSearchTerm = event.bag.values.search && event.bag.values.search.trim()

    if (typingTimeout) {
      clearTimeout(typingTimeout)
    }

    setTypingTimeout(setTimeout(() => {
      if (newSearchTerm !== storedSearchTerm) {
        gettyListStopsReset()
        setStoredSearchTerm(newSearchTerm)
      }
    }, 300))
  }

  return (
    <Modal
      header={<h3>Select Stops</h3>}
      onClose={onCancel}
      footer={(
        <ButtonGroup>
          <Button onClick={onCancel} appearance="secondary">Cancel</Button>
          <Button
            onClick={onSubmit}
            isDisabled={!selectedRelatedStops.length}
          >
            Add Stops
          </Button>
        </ButtonGroup>
      )}
    >
      <SearchContainer>
        <FormData
          bag={formBag}
          onUpdate={onFormUpdate}
        >
          <Input
            label="Search"
            labelHidden={true}
            field="search"
            placeholder="Search"
          />
        </FormData>
      </SearchContainer>
      <SearchTools>
        <span>{
          statusSelector.hasData(stops)
            ? `${stops.data.totalItems} found`
            : null
        }</span>
      </SearchTools>
      <InfiniteScroll
        height={300}
        renderEntity={renderEntityStyles.render}
        requestNextPage={requestPageData}
        statusList={stops}
      />
    </Modal>
  )
}

export const SelectRelatedStops = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => pick(store.getty, 'stopUpdate', 'stops'),
  pick(
    actionCreators, 'gettyListStopsRequest', 'gettyListStopsReset', 'gettyListStopsSearchRequest',
  ),
)(SelectRelatedStopsView);
